import { ReactNode, useId } from 'react';
import { SxProps, Theme, Typography, TypographyProps } from '@mui/material';

import mixinSx from '@watershed/style/mixinSx';

import Button from './Button';
import CollapseIcon, { CollapseIconProps } from './CollapseIcon';

export default function CollapseButton({
  children,
  isExpanded,
  setIsExpanded,
  sx,
  iconProps,
  isIconOnRight,
  typographyProps,
  onClick,
}: {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
  iconProps?: CollapseIconProps;
  typographyProps?: TypographyProps<'span'>;
  isIconOnRight?: boolean;
  onClick?: (event: React.MouseEvent) => void;
}) {
  const contentDomId = useId();
  return (
    <Button
      aria-expanded={isExpanded}
      aria-controls={contentDomId}
      variant="text"
      onClick={(event) => {
        onClick?.(event);
        setIsExpanded(!isExpanded);
      }}
      sx={mixinSx(
        {
          padding: 0,
          gap: 1,
          flexDirection: isIconOnRight ? 'row-reverse' : 'row',
          textAlign: 'left',
          height: 'unset',
          '&:hover, &:focus': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '&:focus-visible': {
            outline: (theme) => `1.5px solid ${theme.palette.primary.main}`,
            outlineOffset: '1.5px',
          },
        },
        sx
      )}
    >
      <CollapseIcon
        size={16}
        {...iconProps}
        sx={mixinSx(iconProps?.sx, {
          flexShrink: 0,
          alignSelf: 'start',
          height: '24px',
        })}
      />
      <Typography
        variant="body2"
        component="span"
        {...typographyProps}
        sx={mixinSx(
          {
            textAlign: 'left',
            flexGrow: 1,
          },
          typographyProps?.sx
        )}
      >
        {children}
      </Typography>
    </Button>
  );
}
